import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Psychology as PsychologyIcon,
  Videocam as VideocamIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material'
import { participantViewService } from '@/services/participantViewService'
import { recordingService, type RecordingTask, type Recording } from '@/services/recordingService'
import type { Project } from '@/services/projectTypes'
import { resolvePlayableVideo } from '@/utils/playableVideo'
import { useLocale } from '@/contexts/LocaleContext'
import { formatDate, formatDateTime } from '@/utils/formatDateTime'
import { getRecordingStatusLabel } from '@/utils/recordingStatus'

function asList<T>(data: T[] | { results?: T[] } | unknown): T[] {
  return Array.isArray(data) ? data : ((data as { results?: T[] })?.results ?? [])
}

interface ProjectWithStatus extends Project {
  questionnaire_completed?: boolean
  questionnaire_started?: boolean
  invite_token?: string
  participant_status?: string
}

export default function ParticipantProjectDetail() {
  const { t } = useTranslation('participant')
  const { t: tRecording } = useTranslation('recording')
  const { t: tCommon } = useTranslation('common')
  const { t: tErrors } = useTranslation('errors')
  const { language } = useLocale()
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<ProjectWithStatus | null>(null)
  const [recordingTasks, setRecordingTasks] = useState<RecordingTask[]>([])
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [videoBlobUrls, setVideoBlobUrls] = useState<Record<number, string>>({})
  const [loadingVideo, setLoadingVideo] = useState<Record<number, boolean>>({})

  useEffect(() => {
    if (projectId) {
      loadProjectData()
    }
  }, [projectId])

  const loadProjectData = async () => {
    if (!projectId) return

    try {
      setLoading(true)
      setError(null)

      const projectIdNum = parseInt(projectId, 10)
      const [projectData, tasksData, recordingsData] = await Promise.all([
        participantViewService.getProjectDetail(projectIdNum),
        recordingService.getRecordingTasks(undefined, projectIdNum).catch(() => []),
        recordingService.getRecordings(undefined, projectIdNum).catch(() => []),
      ])

      setProject({
        ...projectData,
        questionnaire_completed: projectData.questionnaire_completed,
        questionnaire_started: projectData.questionnaire_started,
      })



      const tasksArray = asList<RecordingTask>(tasksData)
      setRecordingTasks(
        tasksArray.filter(
          (task) => task.status === 'pending' || task.status === 'in_progress',
        ),
      )

      setRecordings(asList<Recording>(recordingsData))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const getQuestionnaireStatus = (projectData: ProjectWithStatus) => {
    if (projectData.questionnaire_completed) {
      return { label: t('questionnaireCompleted'), color: 'success' as const }
    }
    if (projectData.questionnaire_started) {
      return { label: t('questionnaireInProgress'), color: 'warning' as const }
    }
    return { label: t('questionnaireNotStarted'), color: 'default' as const }
  }

  const getRecordingTypeLabel = (taskType: string) => {
    return taskType === 'recording_with_video'
      ? t('recordingWithVideo')
      : t('simpleRecording')
  }

  // Launch the full capture journey (consent → camera + stimulus → questionnaire)
  // via the public respondent route. The join call ensures a tracked participant
  // row (creating one for a public study on first start) so the run attributes
  // to this account rather than an anonymous walk-in.
  const handleStartStudy = async () => {
    if (!projectId) return
    try {
      const { share_token, invite_token } = await participantViewService.joinStudy(parseInt(projectId, 10))
      const q = invite_token ? `?invite=${encodeURIComponent(invite_token)}` : ''
      navigate(`/r/${share_token}${q}`)
    } catch {
      if (project?.share_token) navigate(`/r/${project.share_token}`)
    }
  }

  const handleFillQuestionnaire = () => {
    if (projectId) {
      navigate(`/participant/projects/${projectId}/questionnaire`)
    }
  }

  const handleViewResponses = () => {
    if (projectId) {
      navigate(`/participant/projects/${projectId}/responses`)
    }
  }

  const handleExecuteTask = (task: RecordingTask) => {
    const params = new URLSearchParams({ taskId: String(task.id) })
    if (task.note) {
      params.set('note', task.note)
    }
    navigate(`/recording-session?${params.toString()}`)
  }

  const handleViewRecording = useCallback(async (recording: Recording) => {
    if (!recording.storage_url) {
      return
    }

    if (videoBlobUrls[recording.id]) {
      window.open(videoBlobUrls[recording.id], '_blank', 'noopener,noreferrer')
      return
    }

    setLoadingVideo(prev => ({ ...prev, [recording.id]: true }))

    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        throw new Error('No authentication token available')
      }

      // Presigned S3 URL (or blob: URL for local storage) opened directly,
      // instead of streaming the bytes through the app worker first.
      const resolvedUrl = await resolvePlayableVideo(recording.storage_url, token)

      setVideoBlobUrls(prev => ({ ...prev, [recording.id]: resolvedUrl }))
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error('Failed to load authenticated video:', err)
      setError(err instanceof Error ? err.message : tRecording('errors.loadSession'))
    } finally {
      setLoadingVideo(prev => {
        const newState = { ...prev }
        delete newState[recording.id]
        return newState
      })
    }
  }, [videoBlobUrls, tRecording])

  useEffect(() => {
    return () => {
      Object.values(videoBlobUrls).forEach(url => {
        URL.revokeObjectURL(url)
      })
    }
  }, [videoBlobUrls])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !project) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/participant/dashboard')}
          sx={{ mb: 2 }}
        >
          {t('backToProjects')}
        </Button>
        <Alert severity="error">
          {error || tErrors('project_not_found')}
        </Alert>
      </Box>
    )
  }

  const status = getQuestionnaireStatus(project)
  const hasQuestionnaire = !!project.questionnaire_template

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/participant/dashboard')}
        sx={{ mb: 2 }}
      >
        {t('backToProjects')}
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FolderIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">{project.name}</Typography>
        </Box>
        {project.description && (
          <Typography variant="body1" color="text.secondary">
            {project.description}
          </Typography>
        )}
        {project.share_token && project.participant_status !== 'completed' && (
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={() => void handleStartStudy()}
            sx={{ mt: 2 }}
          >
            {project.participant_status === 'in_progress' ? t('continueStudy') : t('startStudy')}
          </Button>
        )}
      </Box>

      {hasQuestionnaire && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6">{t('questionnaireSection')}</Typography>
              <Chip
                icon={project.questionnaire_completed ? <CheckCircleIcon /> : <AssignmentIcon />}
                label={status.label}
                color={status.color}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {!project.questionnaire_completed && (
                <Button
                  variant="contained"
                  startIcon={<AssignmentIcon />}
                  onClick={handleFillQuestionnaire}
                >
                  {project.questionnaire_started ? t('continueQuestionnaire') : t('startQuestionnaire')}
                </Button>
              )}
              {project.questionnaire_completed && (
                <Button
                  variant="outlined"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleViewResponses}
                >
                  {t('viewResponses')}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {recordingTasks.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VideocamIcon color="primary" />
              <Typography variant="h6">{t('recordingTasks')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('recordingTasksPending', { count: recordingTasks.length })}
            </Typography>
            <List>
              {recordingTasks.map((task, index) => (
                <React.Fragment key={task.id}>
                  <ListItem
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1.5, sm: 0 },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PsychologyIcon color="primary" />
                          <Typography variant="h6">
                            {getRecordingTypeLabel(task.task_type)}
                          </Typography>
                          <Chip
                            label={getRecordingStatusLabel(task.status, tRecording)}
                            size="small"
                            color="warning"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {task.note && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {task.note}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {tRecording('createdAt', { date: formatDate(task.created_at, language) })}
                          </Typography>
                        </Box>
                      }
                    />
                    <Button
                      variant="contained"
                      startIcon={<VideocamIcon />}
                      onClick={() => handleExecuteTask(task)}
                      size="small"
                    >
                      {tRecording('startRecording')}
                    </Button>
                  </ListItem>
                  {index < recordingTasks.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {recordings.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">{t('completedRecordings')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('recordingsCompleted', { count: recordings.length })}
            </Typography>
            <List>
              {recordings.map((recording, index) => (
                <React.Fragment key={recording.id}>
                  <ListItem
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1.5, sm: 0 },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VideocamIcon color="success" />
                          <Typography variant="h6">
                            {t('recordingItem', { id: recording.id, type: recording.type })}
                          </Typography>
                          <Chip label={t('completed')} size="small" color="success" />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {recording.note && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {recording.note}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              {tRecording('createdAt', { date: formatDateTime(recording.created_at, language) })}
                            </Typography>
                            {recording.duration_seconds && (
                              <Typography variant="caption" color="text.secondary">
                                {recording.duration_seconds}s
                              </Typography>
                            )}
                            {recording.fps && (
                              <Typography variant="caption" color="text.secondary">
                                {recording.fps.toFixed(2)} FPS
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    {recording.storage_url && (
                      <Button
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleViewRecording(recording)}
                        disabled={loadingVideo[recording.id]}
                        size="small"
                      >
                        {loadingVideo[recording.id] ? tCommon('loading') : tCommon('view')}
                      </Button>
                    )}
                  </ListItem>
                  {index < recordings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {!hasQuestionnaire && recordingTasks.length === 0 && recordings.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {t('noQuestionnaireAssigned')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
